/**
 * 2024 Tax Year Configuration
 * 
 * Federal tax brackets, capital gains rates, and standard deductions.
 * Source: IRS Rev. Proc. 2023-34
 */

import { FederalTaxConfig } from '../../types.js';

export const federal2024: FederalTaxConfig = {
  brackets: {
    single: [
      { min: 0, max: 11600, rate: 0.10 },
      { min: 11600, max: 47150, rate: 0.12 },
      { min: 47150, max: 100525, rate: 0.22 },
      { min: 100525, max: 191950, rate: 0.24 },
      { min: 191950, max: 243725, rate: 0.32 },
      { min: 243725, max: 609350, rate: 0.35 },
      { min: 609350, max: Infinity, rate: 0.37 }
    ],
    marriedFilingJointly: [
      { min: 0, max: 23200, rate: 0.10 },
      { min: 23200, max: 94300, rate: 0.12 },
      { min: 94300, max: 201050, rate: 0.22 },
      { min: 201050, max: 383900, rate: 0.24 },
      { min: 383900, max: 487450, rate: 0.32 },
      { min: 487450, max: 731200, rate: 0.35 },
      { min: 731200, max: Infinity, rate: 0.37 }
    ],
    marriedFilingSeparately: [
      { min: 0, max: 11600, rate: 0.10 },
      { min: 11600, max: 47150, rate: 0.12 },
      { min: 47150, max: 100525, rate: 0.22 },
      { min: 100525, max: 191950, rate: 0.24 },
      { min: 191950, max: 243725, rate: 0.32 },
      { min: 243725, max: 365600, rate: 0.35 },
      { min: 365600, max: Infinity, rate: 0.37 }
    ],
    headOfHousehold: [
      { min: 0, max: 16550, rate: 0.10 },
      { min: 16550, max: 63100, rate: 0.12 },
      { min: 63100, max: 100500, rate: 0.22 },
      { min: 100500, max: 191950, rate: 0.24 },
      { min: 191950, max: 243700, rate: 0.32 },
      { min: 243700, max: 609350, rate: 0.35 },
      { min: 609350, max: Infinity, rate: 0.37 }
    ]
  },
  
  capitalGainsBrackets: {
    single: [
      { min: 0, max: 47025, rate: 0.00 },
      { min: 47025, max: 518900, rate: 0.15 },
      { min: 518900, max: Infinity, rate: 0.20 }
    ],
    marriedFilingJointly: [
      { min: 0, max: 94050, rate: 0.00 },
      { min: 94050, max: 583750, rate: 0.15 },
      { min: 583750, max: Infinity, rate: 0.20 }
    ],
    marriedFilingSeparately: [
      { min: 0, max: 47025, rate: 0.00 },
      { min: 47025, max: 291850, rate: 0.15 },
      { min: 291850, max: Infinity, rate: 0.20 }
    ],
    headOfHousehold: [
      { min: 0, max: 63000, rate: 0.00 },
      { min: 63000, max: 551350, rate: 0.15 },
      { min: 551350, max: Infinity, rate: 0.20 }
    ]
  },
  
  standardDeductions: {
    single: 14600,
    marriedFilingJointly: 29200,
    marriedFilingSeparately: 14600,
    headOfHousehold: 21900
  },
  
  niitThresholds: {
    single: 200000,
    marriedFilingJointly: 250000,
    marriedFilingSeparately: 125000,
    headOfHousehold: 200000
  },
  
  niitRate: 0.038,
  
  socialSecurityWageBase: 168600,
  socialSecurityRate: 0.062,
  
  medicareRate: 0.0145,
  additionalMedicareRate: 0.009,
  additionalMedicareThresholds: {
    single: 200000,
    marriedFilingJointly: 250000,
    marriedFilingSeparately: 125000,
    headOfHousehold: 200000
  },

  // AMT (Alternative Minimum Tax) - IRS Rev. Proc. 2023-34
  amtExemptions: {
    single: 85700,
    marriedFilingJointly: 133300,
    marriedFilingSeparately: 66650,
    headOfHousehold: 85700
  },
  amtPhaseOutThresholds: {
    single: 609350,
    marriedFilingJointly: 1218700,
    marriedFilingSeparately: 609350,
    headOfHousehold: 609350
  },
  amtPhaseOutRate: 0.25,
  // 26%/28% rate breakpoint (MFS is half of the general threshold)
  amtHighRateThreshold: {
    single: 232600,
    marriedFilingJointly: 232600,
    marriedFilingSeparately: 116300,
    headOfHousehold: 232600
  },
  amtRates: [0.26, 0.28],
  // Itemized deductions (TCJA SALT cap; no phase-down)
  itemizedDeductions: {
    saltCap: 10000,
    saltPhaseDownThreshold: null,
    saltPhaseDownRate: 0,
    saltFloor: 10000,
    charitableAgiFloorRate: 0,
    nonItemizerCharitableCap: { single: 0, marriedFilingJointly: 0 },
    itemizedBenefitLimitRate: 0
  },

  // IRS Notice 2023-75
  retirement401kLimit: 23000,
  retirement401kCatchup: 7500
};
